<!--
/**
 * @component HeaderProfileMenu
 * @description Menu de profil utilisateur avec avatar et actions (paramètres, déconnexion, etc.)
 * @example
 * <HeaderProfileMenu />
 */
-->
<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useEventBus } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { useAuthStore } from "@core/stores/auth.store"
import { SUPPORT_OPEN_EVENT } from "@core/components/support/support.events"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            profile: {
                fullName: "{firstname} {lastname}",
                companyInfo: "{type} chez {company}",
            },
            roles: {
                employe: "Membre",
                administrateur: "Admin",
                owner: "Super Admin",
                employe_read_only: "Utilisateur",
                default: "Membre",
            },
            menu: {
                companySpace: "Espace entreprise",
                myInfo: "Mes informations",
                consents: "Consentements",
                reportBug: "Signaler un bug",
                shareFeedback: "Partager votre avis",
                faq: "F.A.Q",
                changeCompany: "Changer d'espace entreprise",
                logout: "Se déconnecter",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const authStore = useAuthStore()
const router = useRouter()
const busSupportOpen = useEventBus<void>(SUPPORT_OPEN_EVENT)

// ================================
// STATE
// ================================

/** Référence vers le menu avatar */
const avatarMenuRef = ref()

// ================================
// COMPUTED
// ================================

/**
 * URL de l'image de profil de l'utilisateur
 */
const getUserImage = computed(() => authStore.img)

/**
 * Initiales de l'utilisateur (première lettre du prénom + première lettre du nom)
 */
const getUserInitials = computed(() => {
    const first = authStore.firstname?.substring(0, 1) || ""
    const last = authStore.lastname?.substring(0, 1) || ""
    return `${first}${last}`.toUpperCase()
})

/**
 * Nom complet de l'utilisateur
 */
const getUserFullName = computed(() => {
    return t("profile.fullName", {
        firstname: authStore.firstname,
        lastname: authStore.lastname,
    })
})

/**
 * Email/username de l'utilisateur
 */
const getUserEmail = computed(() => authStore.username)

/**
 * Rôle de l'utilisateur traduit
 */
const getUserRoleLabel = computed(() => {
    const role = authStore.role
    if (!role) return t("roles.default")

    // Mapper le rôle vers la clé de traduction
    const roleKey = `roles.${role}`
    return t(roleKey, t("roles.default"))
})

/**
 * Informations entreprise formatées
 */
const getUserCompanyInfo = computed(() => {
    if (!authStore.companyName) return null
    return t("profile.companyInfo", {
        type: getUserRoleLabel.value,
        company: authStore.companyName,
    })
})

// ================================
// DONNÉES CALCULÉES (MENU)
// ================================

/**
 * Items du menu principal
 */
const getMainMenuItems = computed(() => {
    const items = []

    // Espace entreprise (uniquement pour Client Homeys ou UC2 Pro)
    if (authStore.isClientHomeys || authStore.isUc2Pro) {
        items.push({
            label: t("menu.companySpace"),
            icon: "pi pi-building",
            command: () => onNavigateTo("/user/entreprise"),
        })
    }

    // Mes informations
    items.push({
        label: t("menu.myInfo"),
        icon: "pi pi-user",
        command: () => onNavigateTo("/user/settings"),
    })

    // Consentements
    items.push({
        label: t("menu.consents"),
        icon: "pi pi-file",
        command: () => onNavigateTo("/consentements"),
    })

    return items
})

/**
 * Items du menu support (uniquement pour employés Homeys avec droits d'écriture)
 */
const getSupportMenuItems = computed(() => {
    if (!authStore.isEmployeWriteOrHigher || !authStore.isClientHomeys) {
        return []
    }

    return [
        {
            label: t("menu.reportBug"),
            icon: "pi pi-exclamation-circle",
            command: () => onOpenSupport("BUG"),
        },
        {
            label: t("menu.shareFeedback"),
            icon: "pi pi-comment",
            command: () => onOpenSupport("FEEDBACK"),
        },
        {
            label: t("menu.faq"),
            icon: "pi pi-question-circle",
            command: () => onOpenFAQ(),
        },
    ]
})

/**
 * Items du menu déconnexion
 */
const getLogoutMenuItems = computed(() => [
    {
        label: t("menu.logout"),
        icon: "pi pi-sign-out",
        command: () => onLogout(),
    },
])

/**
 * Options pour le changement d'entreprise
 */
const getCompanyOptions = computed(() => {
    if (!authStore.hasMultipleCompanies) return []
    return authStore.getMultipleCompanies.map((company) => ({
        label: company.name,
        value: company._id,
    }))
})

/**
 * ID de l'entreprise actuellement sélectionnée
 */
const getCurrentCompanyId = computed(() => authStore.companyId)

/**
 * Structure du menu avec séparateurs entre les catégories
 */
const getMenuStructure = computed(() => {
    return {
        main: getMainMenuItems.value,
        support: getSupportMenuItems.value,
        changeCompany: getCompanyOptions.value,
        logout: getLogoutMenuItems.value,
    }
})

// ================================
// INTERACTIONS
// ================================

/**
 * Toggle du menu avatar
 */
function onToggleAvatarMenu(event: Event) {
    avatarMenuRef.value?.toggle(event)
}

/**
 * Navigation vers une route
 */
function onNavigateTo(path: string) {
    router.push(path)
    avatarMenuRef.value?.hide()
}

/**
 * Ouvre le modal de support (écoute via event bus dans SupportButton).
 */
function onOpenSupport(_type: "BUG" | "FEEDBACK") {
    busSupportOpen.emit()
    avatarMenuRef.value?.hide()
}

/**
 * Ouvre la FAQ dans un nouvel onglet
 */
function onOpenFAQ() {
    window.open("https://www.homeys.fr/support", "_blank")?.focus()
    avatarMenuRef.value?.hide()
}

/**
 * Changement d'entreprise
 */
function onChangeCompany(companyId: string) {
    if (authStore.companyId === companyId) {
        return
    }

    avatarMenuRef.value?.hide()

    router.push({
        path: "/change-space",
        query: { company: companyId },
    })
}

/**
 * Déconnexion de l'utilisateur
 */
async function onLogout() {
    avatarMenuRef.value?.hide()
    router.push("/login").then(() => {
        authStore.logout()
    })
}
</script>

<template>
    <!-- ================================ -->
    <!-- MENU PROFIL UTILISATEUR -->
    <!-- ================================ -->
    <div class="header-profile-menu relative flex items-center justify-center">
        <!-- Avatar cliquable -->
        <div
            class="w-10 h-10 rounded-full flex items-center justify-center cursor-pointer border-2 border-white transition-all duration-200 hover:shadow-md hover:scale-105"
            :class="getUserImage ? 'bg-transparent' : 'bg-white/20'"
            @click="onToggleAvatarMenu"
        >
            <span
                v-if="!getUserImage"
                class="text-sm font-semibold text-white uppercase select-none"
            >
                {{ getUserInitials }}
            </span>
            <img
                v-else
                :src="getUserImage"
                :alt="getUserFullName"
                class="w-full h-full rounded-full object-cover"
            />
        </div>

        <!-- Menu déroulant -->
        <BaseMenu
            ref="avatarMenuRef"
            :model="[]"
            :popup="true"
            :pt="{
                root: { class: 'min-w-[18rem]! max-w-[18rem]! z-[2000]!' },
            }"
        >
            <template #start>
                <!-- ================================ -->
                <!-- SECTION PROFIL -->
                <!-- ================================ -->
                <div
                    class="flex items-center gap-4 p-3 border-b border-surface-200"
                >
                    <!-- Avatar profil -->
                    <div
                        class="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 border-2"
                        :class="
                            getUserImage
                                ? 'bg-transparent border-none'
                                : 'bg-primary-50 border-primary-200'
                        "
                    >
                        <span
                            v-if="!getUserImage"
                            class="text-sm font-semibold uppercase select-none"
                            :class="
                                getUserImage ? 'text-white' : 'text-primary-600'
                            "
                        >
                            {{ getUserInitials }}
                        </span>
                        <img
                            v-else
                            :src="getUserImage"
                            :alt="getUserFullName"
                            class="w-full h-full rounded-full object-cover"
                        />
                    </div>

                    <!-- Informations utilisateur -->
                    <div class="flex flex-col gap-0.5 min-w-0 flex-1">
                        <p
                            class="font-semibold text-surface-900 overflow-hidden text-ellipsis whitespace-nowrap m-0"
                            :title="getUserFullName"
                        >
                            {{ getUserFullName }}
                        </p>
                        <p
                            class="text-sm text-surface-600 overflow-hidden text-ellipsis whitespace-nowrap m-0"
                            :title="getUserEmail"
                        >
                            {{ getUserEmail }}
                        </p>
                        <p
                            v-if="getUserCompanyInfo"
                            class="text-xs font-light text-surface-600 overflow-hidden text-ellipsis whitespace-nowrap m-0"
                            :title="getUserCompanyInfo"
                        >
                            {{ getUserCompanyInfo }}
                        </p>
                    </div>
                </div>
            </template>

            <template #end>
                <!-- ================================ -->
                <!-- MENU PRINCIPAL -->
                <!-- ================================ -->
                <div
                    v-if="getMenuStructure.main.length"
                    class="py-2 border-b border-surface-200"
                >
                    <a
                        v-for="item in getMenuStructure.main"
                        :key="item.label"
                        v-ripple
                        class="flex items-center gap-3 px-3 py-2.5 mx-2 cursor-pointer transition-colors duration-200 rounded-md text-surface-900 hover:bg-surface-100 no-underline"
                        @click="item.command"
                    >
                        <BaseIcon v-if="item.icon" :icon="item.icon" />
                        <span
                            class="text-sm font-normal overflow-hidden text-ellipsis whitespace-nowrap"
                        >
                            {{ item.label }}
                        </span>
                    </a>
                </div>

                <!-- ================================ -->
                <!-- MENU SUPPORT -->
                <!-- ================================ -->
                <div
                    v-if="getMenuStructure.support.length"
                    class="py-2 border-b border-surface-200"
                >
                    <a
                        v-for="item in getMenuStructure.support"
                        :key="item.label"
                        v-ripple
                        class="flex items-center gap-3 px-3 py-2.5 mx-2 cursor-pointer transition-colors duration-200 rounded-md text-surface-900 hover:bg-surface-100 no-underline"
                        @click="item.command"
                    >
                        <BaseIcon v-if="item.icon" :icon="item.icon" />
                        <span
                            class="text-sm font-normal overflow-hidden text-ellipsis whitespace-nowrap"
                        >
                            {{ item.label }}
                        </span>
                    </a>
                </div>

                <!-- ================================ -->
                <!-- CHANGEMENT D'ENTREPRISE -->
                <!-- ================================ -->
                <div
                    v-if="getMenuStructure.changeCompany.length"
                    class="py-2 border-b border-surface-200"
                >
                    <div class="px-3 py-2 mx-2">
                        <span
                            class="text-sm font-light text-surface-600 block mb-2"
                        >
                            {{ t("menu.changeCompany") }}
                        </span>
                        <BaseSelect
                            :model-value="getCurrentCompanyId"
                            :options="getMenuStructure.changeCompany"
                            option-label="label"
                            option-value="value"
                            class="w-full"
                            @update:model-value="onChangeCompany"
                        />
                    </div>
                </div>

                <!-- ================================ -->
                <!-- MENU DÉCONNEXION -->
                <!-- ================================ -->
                <div v-if="getMenuStructure.logout.length" class="py-2">
                    <a
                        v-for="item in getMenuStructure.logout"
                        :key="item.label"
                        v-ripple
                        class="flex items-center gap-3 px-3 py-2.5 mx-2 cursor-pointer transition-colors duration-200 rounded-md text-red-600 hover:bg-red-50 no-underline"
                        @click="item.command"
                    >
                        <BaseIcon v-if="item.icon" :icon="item.icon" />
                        <span
                            class="text-sm font-normal overflow-hidden text-ellipsis whitespace-nowrap"
                        >
                            {{ item.label }}
                        </span>
                    </a>
                </div>
            </template>
        </BaseMenu>
    </div>
</template>
